import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { adById } from '../data/ads';
import { useInView, useTabHidden } from '../hooks/useInView';
import { DemoDescription, ExampleTag, OfferDemoHandle } from './OfferDemoShared';

/** Real, vertical spots from the library - not landscape, not the closing "yours" card. */
const AD_IDS = ['jk-one-message', 'arceus-seo-story', 'jk-everything', 'hoverboard-v2', 'ironload-v3', 'the-climb'] as const;
const AD_POSTERS = AD_IDS.reduce<{ id: string; poster: string }[]>((acc, id) => {
  const card = adById(id);
  if (card?.poster) acc.push({ id: card.id, poster: card.poster });
  return acc;
}, []);
const CYCLE_MS = 1700;

/**
 * What the "Ads" offer card actually does: a small phone crossfades through
 * the real ad posters we run, labeled with the same lead count the hero
 * report shows for that source, so the two numbers on the page agree. It
 * loops while visible and pauses off-screen or in a background tab.
 */
const OfferDemoAds = forwardRef<OfferDemoHandle, object>((_props, ref) => {
  const reduced = useReducedMotion();
  const { ref: viewRef, inView } = useInView<HTMLDivElement>(0.4);
  const tabHidden = useTabHidden();
  const [index, setIndex] = useState(0);

  useImperativeHandle(ref, () => ({
    replay: () => setIndex(0),
  }));

  useEffect(() => {
    if (reduced || !inView || tabHidden || AD_POSTERS.length < 2) return undefined;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % AD_POSTERS.length);
    }, CYCLE_MS);
    return () => window.clearInterval(id);
  }, [reduced, inView, tabHidden]);

  const shownIndex = reduced ? 0 : index;
  const shown = AD_POSTERS[shownIndex];

  return (
    <div className="offerdemo offerdemo--ads" ref={viewRef}>
      <DemoDescription>
        Example: an Instagram intro-offer ad cycling through recent spots we ran, which
        brought in 14 leads this week.
      </DemoDescription>
      <ExampleTag />
      <div className="offerdemo__adsrow" aria-hidden="true">
        <div className="offerdemo__phone">
          {AD_POSTERS.map((card) => (
            <img
              key={card.id}
              src={card.poster}
              alt=""
              loading="lazy"
              decoding="async"
              className={`offerdemo__poster${card.id === shown?.id ? ' is-shown' : ''}`}
            />
          ))}
          {AD_POSTERS.length > 1 && (
            <span className="offerdemo__dots">
              {AD_POSTERS.map((card, i) => (
                <i key={card.id} className={i === shownIndex ? 'is-active' : ''} />
              ))}
            </span>
          )}
        </div>
        <div className="offerdemo__meta">
          <p className="offerdemo__metaname">Instagram intro-offer ad</p>
          <p className="offerdemo__metanote">14 leads this week</p>
        </div>
      </div>
    </div>
  );
});

OfferDemoAds.displayName = 'OfferDemoAds';

export default React.memo(OfferDemoAds);
